import { z } from "zod";
import { consultaPaginadaSchema, paginaSchema } from "../common/pagination";

/**
 * Contratos de usuarios, sessao e senha. Os mesmos schemas validam a requisicao
 * na API e a resposta no front.
 */

export const PAPEIS = ["admin", "usuario"] as const;
export const papelSchema = z.enum(PAPEIS);
export type Papel = z.infer<typeof papelSchema>;

/** Escopos que o papel admin concede. Usuario comum nao tem escopo extra. */
export const ESCOPOS_DE_USUARIOS = {
  ler: "usuarios:ler",
  escrever: "usuarios:escrever",
} as const;

/** E-mail normalizado: sem espacos nas pontas e em minusculas. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .pipe(z.email("informe um e-mail valido").max(254, "e-mail longo demais"));

export const SENHA_MINIMO = 10;
export const SENHA_MAXIMO = 128;

/**
 * Senha nova. Tamanho minimo e o que mais pesa contra adivinhacao; o maximo
 * existe porque derivar hash de texto gigante e um jeito barato de derrubar a API.
 */
export const senhaNovaSchema = z
  .string()
  .min(SENHA_MINIMO, `minimo de ${SENHA_MINIMO} caracteres`)
  .max(SENHA_MAXIMO, `maximo de ${SENHA_MAXIMO} caracteres`);

/** Senha no login: so limita o tamanho. A regra de forca vale ao definir. */
export const credenciaisSchema = z
  .object({
    email: emailSchema,
    senha: z.string().min(1, "informe a senha").max(SENHA_MAXIMO),
  })
  .strict();
export type Credenciais = z.infer<typeof credenciaisSchema>;

export const usuarioSchema = z.object({
  id: z.uuid(),
  nome: z.string(),
  email: z.string(),
  papel: papelSchema,
  ativo: z.boolean(),
  /** `false` enquanto o convidado nao definiu a primeira senha. */
  senhaDefinida: z.boolean(),
  /** Telefone/WhatsApp so com digitos (DDD + numero, com ou sem 55). */
  telefone: z.string().nullable(),
  /** Cargo na clinica, texto livre ("Recepcao", "Gerente"...). */
  cargo: z.string().nullable(),
  /** `false` ate a pessoa preencher os dados do primeiro login. */
  perfilCompleto: z.boolean(),
  criadoEm: z.iso.datetime(),
  atualizadoEm: z.iso.datetime(),
});
export type Usuario = z.infer<typeof usuarioSchema>;

export const paginaDeUsuariosSchema = paginaSchema(usuarioSchema);
export type PaginaDeUsuarios = z.infer<typeof paginaDeUsuariosSchema>;

/** Situacao da conta na lista: quem ja entra, quem ainda nao definiu senha, quem foi desligado. */
export const SITUACOES_DA_CONTA = ["ativo", "convite", "desativado"] as const;
export const situacaoDaContaSchema = z.enum(SITUACOES_DA_CONTA);
export type SituacaoDaConta = z.infer<typeof situacaoDaContaSchema>;

/** GET /usuarios: busca por nome/e-mail e filtros de papel e situacao. */
export const consultaDeUsuariosSchema = consultaPaginadaSchema
  .pick({ cursor: true, limite: true })
  .extend({
    busca: z.string().trim().max(120).optional(),
    papel: papelSchema.optional(),
    situacao: situacaoDaContaSchema.optional(),
  })
  .strict();
export type ConsultaDeUsuarios = z.infer<typeof consultaDeUsuariosSchema>;

/** Convite ou redefinicao: o admin copia o mesmo link que foi no e-mail. */
export const linkDeSenhaEmitidoSchema = z.object({
  linkDeSenha: z.url(),
});
export type LinkDeSenhaEmitido = z.infer<typeof linkDeSenhaEmitidoSchema>;

/** POST /usuarios: a conta criada mais o link para definir a senha. */
export const usuarioComConviteSchema = usuarioSchema.extend({
  linkDeSenha: z.url(),
});
export type UsuarioComConvite = z.infer<typeof usuarioComConviteSchema>;

export const sessaoAtualSchema = z.object({
  usuario: usuarioSchema.pick({
    id: true,
    nome: true,
    email: true,
    papel: true,
    telefone: true,
    cargo: true,
    perfilCompleto: true,
  }),
  expiraEm: z.iso.datetime(),
});
export type SessaoAtual = z.infer<typeof sessaoAtualSchema>;

const nomeSchema = z
  .string()
  .trim()
  .min(3, "minimo de 3 caracteres")
  .max(120, "maximo de 120 caracteres");

export const criarUsuarioSchema = z
  .object({
    nome: nomeSchema,
    email: emailSchema,
    papel: papelSchema.default("usuario"),
  })
  .strict();
export type CriarUsuario = z.infer<typeof criarUsuarioSchema>;

export const atualizarUsuarioSchema = z
  .object({
    nome: nomeSchema.optional(),
    papel: papelSchema.optional(),
    ativo: z.boolean().optional(),
  })
  .strict()
  .refine(
    (dados) => Object.values(dados).some((valor) => valor !== undefined),
    { message: "informe ao menos um campo para alterar" },
  );
export type AtualizarUsuario = z.infer<typeof atualizarUsuarioSchema>;

export const esqueciSenhaSchema = z.object({ email: emailSchema }).strict();
export type EsqueciSenha = z.infer<typeof esqueciSenhaSchema>;

export const redefinirSenhaSchema = z
  .object({
    token: z.string().min(20, "link invalido").max(200, "link invalido"),
    novaSenha: senhaNovaSchema,
  })
  .strict();
export type RedefinirSenha = z.infer<typeof redefinirSenhaSchema>;

/**
 * Telefone brasileiro: aceita mascara ("(11) 98765-4321", "+55 11 ...") e
 * guarda so os digitos, com DDD (10 ou 11 digitos, ou 12/13 com o 55).
 */
export const telefoneSchema = z
  .string()
  .transform((valor) => valor.replace(/\D/g, ""))
  .pipe(z.string().regex(/^(55)?\d{10,11}$/, "informe DDD + número, como (11) 98765-4321"));

/** O que a propria pessoa edita no perfil (e preenche no primeiro login). */
export const atualizarPerfilSchema = z
  .object({
    nome: nomeSchema,
    telefone: telefoneSchema,
    cargo: z.string().trim().min(2, "informe o cargo").max(80, "maximo de 80 caracteres"),
  })
  .strict();
export type AtualizarPerfil = z.infer<typeof atualizarPerfilSchema>;
